import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Instagram, Youtube, Linkedin, Send, Quote, Loader2, MapPin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageTransition from "../components/PageTransition";
import { submitContact } from "../lib/api";

const PROJECT_TYPES = ["Brand Film", "Corporate Film", "Spec Ad", "Short Film", "Other"];
const BUDGETS = [
  "Under $5K",
  "$5K – $15K",
  "$15K – $50K",
  "$50K – $100K",
  "$100K+",
  "Let's discuss",
];

/* ------------------ Animated Particle Field ------------------ */
const Particles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 28 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 1 + Math.random() * 2.5,
        duration: 18 + Math.random() * 24,
        delay: Math.random() * -20,
        drift: 60 + Math.random() * 180,
        opacity: 0.25 + Math.random() * 0.55,
      })),
    []
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-gold"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            opacity: d.opacity,
            filter: "blur(0.6px)",
          }}
          animate={{ y: [0, -d.drift, 0], opacity: [d.opacity * 0.4, d.opacity, d.opacity * 0.4] }}
          transition={{
            duration: d.duration,
            delay: d.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

/* ------------------ Header ------------------ */
const Header = () => (
  <section className="relative pt-40 pb-12 md:pt-52 md:pb-16" data-testid="contact-header">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12 relative z-10">
      <p className="overline mb-6">Begin a Project · Now booking</p>
      <div className="overflow-hidden">
        <motion.h1
          initial={{ y: "8%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 0.65, 0.2, 1] }}
          className="font-serif text-bone leading-[0.92] tracking-tight max-w-6xl"
          style={{ fontSize: "clamp(2.4rem, 7.6vw, 7.5rem)" }}
          data-testid="contact-headline"
        >
          Let's make something <span className="italic text-gold">cinematic</span> together.
        </motion.h1>
      </div>
      <p className="mt-8 text-bone/75 font-sans text-base md:text-xl max-w-2xl leading-relaxed" data-testid="contact-subheadline">
        Have a brand story to tell? A vision that deserves a film?
      </p>
      <div className="hairline mt-12 md:mt-16" />
    </div>
  </section>
);

/* ------------------ Left: Studio Details ------------------ */
const StudioDetails = () => (
  <div className="lg:col-span-5 lg:sticky lg:top-32 self-start" data-testid="contact-details">
    <p className="overline mb-5">The Studio · Direct line</p>
    <h2
      className="font-serif text-bone leading-[1.05] tracking-tight"
      style={{ fontSize: "clamp(1.7rem, 2.6vw, 2.4rem)" }}
    >
      Write the studio. <br /> We answer in <span className="italic text-gold">light</span>.
    </h2>

    <div className="hairline my-10" />

    {/* Founder + Studio */}
    <div className="mb-8">
      <p className="overline mb-3">Founder</p>
      <p className="font-serif text-bone text-2xl">Samresh Shrivastav</p>
      <p className="text-bone/60 font-sans text-sm mt-1">Independent AI Filmmaker & Creator</p>
    </div>

    {/* Email */}
    <div className="mb-8">
      <p className="overline mb-3">Email</p>
      <a
        href="mailto:samresh.shrivastav@gmail.com"
        className="group inline-flex items-center gap-3 text-bone hover:text-gold transition-colors"
        data-testid="contact-email-link"
      >
        <Mail size={16} className="text-gold" />
        <span className="font-serif text-xl md:text-2xl break-all">
          <span className="cp-title-underline">samresh.shrivastav@gmail.com</span>
        </span>
      </a>
    </div>

    {/* Studio location */}
    <div className="mb-8">
      <p className="overline mb-3">Studio</p>
      <ul className="flex flex-col gap-2 text-bone/75 font-sans">
        <li className="inline-flex items-center gap-3">
          <MapPin size={14} className="text-gold" />
          Cine Prompt · Hyderabad, India
        </li>
      </ul>
    </div>

    <div className="hairline my-10" />

    {/* Social */}
    <div>
      <p className="overline mb-4">Channels</p>
      <ul className="flex items-center gap-3" data-testid="contact-socials">
        {[
          { name: "Instagram", Icon: Instagram, href: "#" },
          { name: "YouTube", Icon: Youtube, href: "https://youtube.com/@apocalypto22" },
          { name: "LinkedIn", Icon: Linkedin, href: "#" },
        ].map(({ name, Icon, href }) => {
          const isExternal = href.startsWith("http");
          return (
            <li key={name}>
              <a
                href={href}
                aria-label={name}
                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`contact-social-${name.toLowerCase()}`}
                className="group inline-flex items-center justify-center w-12 h-12 border border-bone/15 hover:border-gold hover:bg-gold transition-all duration-500"
              >
                <Icon
                  size={16}
                  className="text-bone/75 group-hover:text-ink transition-colors duration-500"
                  strokeWidth={1.4}
                />
              </a>
            </li>
          );
        })}
      </ul>
    </div>

    <div className="hairline my-10" />

    {/* Mood quote */}
    <figure className="relative pl-6" data-testid="contact-quote">
      <span className="absolute left-0 top-0 bottom-0 w-px bg-gold/60" aria-hidden="true" />
      <Quote className="text-gold mb-4" size={20} strokeWidth={1.2} />
      <blockquote
        className="font-serif italic text-bone leading-[1.15]"
        style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.2rem)" }}
      >
        Every great film starts with <span className="text-gold">a single prompt</span>.
      </blockquote>
      <figcaption className="overline mt-5">— Cine Prompt</figcaption>
      <p className="mt-6 text-bone/55 font-sans text-sm leading-relaxed max-w-sm">
        For brand films, corporate films, spec ads, and collaborations — reach out and
        let's build something worth watching.
      </p>
    </figure>
  </div>
);

/* ------------------ Right: Form ------------------ */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      const meta = [form.company, form.project_type, form.budget].filter(Boolean).join(" · ");
      await submitContact({
        name: form.name,
        email: form.email,
        project_type: meta || null,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError("Something went wrong. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="lg:col-span-7 relative border border-gold/40 bg-ink p-10 md:p-16 overflow-hidden"
        data-testid="contact-form-success"
      >
        <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gold" />
        <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gold" />
        <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gold" />
        <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gold" />
        <p className="overline mb-6">Conversation started</p>
        <h3
          className="font-serif text-bone leading-[1] tracking-tight"
          style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)" }}
        >
          Your story is in <span className="italic text-gold">the dark room</span>.
        </h3>
        <p className="mt-6 text-bone/70 font-sans text-base md:text-lg leading-relaxed max-w-xl">
          Samresh reads everything by hand. Expect a reply within two business days — usually
          with a question, often with a film still attached.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
          }}
          className="cp-btn cp-btn--ghost mt-10 inline-flex"
          data-testid="contact-form-reset"
        >
          Send another note
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="lg:col-span-7 relative border border-gold/30 bg-ink/70 backdrop-blur-sm p-8 md:p-10 lg:p-14 overflow-hidden"
      data-testid="contact-form"
    >
      {/* Corner accents */}
      <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gold/60" />
      <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gold/60" />
      <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gold/60" />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gold/60" />

      <p className="overline mb-2">Project Brief — 01</p>
      <h2
        className="font-serif text-bone leading-[1] tracking-tight mb-10"
        style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}
      >
        Tell us your <span className="italic text-gold">story</span>.
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        {/* Name */}
        <div>
          <label htmlFor="cp-name" className="overline mb-3 block">Your Name</label>
          <input
            id="cp-name"
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            className="cp-input"
            data-testid="contact-input-name"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="cp-email" className="overline mb-3 block">Your Email</label>
          <input
            id="cp-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@yourbrand.com"
            className="cp-input"
            data-testid="contact-input-email"
          />
        </div>

        {/* Company / Brand */}
        <div className="md:col-span-2">
          <label htmlFor="cp-company" className="overline mb-3 block">Company / Brand Name</label>
          <input
            id="cp-company"
            type="text"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder="Your company or brand"
            className="cp-input"
            data-testid="contact-input-company"
          />
        </div>

        {/* Project Type */}
        <div>
          <label className="overline mb-3 block">Project Type</label>
          <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
            <SelectTrigger
              data-testid="contact-select-project-type"
              className="cp-select-trigger"
            >
              <SelectValue placeholder="Brand Film / Corporate / Spec Ad / Short Film / Other" />
            </SelectTrigger>
            <SelectContent className="cp-select-content">
              {PROJECT_TYPES.map((t) => (
                <SelectItem key={t} value={t} className="cp-select-item" data-testid={`project-type-${t.toLowerCase().replace(/ /g, "-")}`}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Budget */}
        <div>
          <label className="overline mb-3 block">Budget Range</label>
          <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
            <SelectTrigger
              data-testid="contact-select-budget"
              className="cp-select-trigger"
            >
              <SelectValue placeholder="Choose a range" />
            </SelectTrigger>
            <SelectContent className="cp-select-content">
              {BUDGETS.map((b) => (
                <SelectItem key={b} value={b} className="cp-select-item">
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Message */}
        <div className="md:col-span-2">
          <label htmlFor="cp-message" className="overline mb-3 block">Tell us your story (What do you want to make?)</label>
          <textarea
            id="cp-message"
            required
            rows={6}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder="Tell us about the film you're dreaming — references, timeline, the feeling you want a viewer to leave with."
            className="cp-input cp-textarea"
            data-testid="contact-input-message"
          />
        </div>
      </div>

      {error && (
        <p className="mt-6 text-red-300 font-sans text-sm" data-testid="contact-form-error">
          {error}
        </p>
      )}

      <div className="hairline mt-10 mb-8" />

      <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6">
        <p className="text-bone/45 text-[10px] tracking-[0.3em] uppercase">
          Every great film begins with a single conversation.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="cp-btn cp-btn--glow disabled:opacity-70"
          data-testid="contact-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Sending
            </>
          ) : (
            <>
              Start the Conversation <ArrowUpRight size={14} />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/* ------------------ Main split section ------------------ */
const ContactSplit = () => (
  <section className="relative pb-24 md:pb-32" data-testid="contact-split">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <StudioDetails />
        <ContactForm />
      </div>
    </div>
  </section>
);

/* ------------------ Closing band ------------------ */
const ClosingBand = () => (
  <section className="cp-noise-cta py-24 md:py-32 border-y border-gold/30" data-testid="contact-partnerships">
    <div className="relative z-10 mx-auto max-w-[1300px] px-6 md:px-12 text-center">
      <p className="overline mb-8">Closing note · From the studio</p>
      <Quote className="text-gold mx-auto mb-8" size={26} strokeWidth={1.2} />
      <blockquote
        className="font-serif italic text-gold leading-[1] tracking-tight mx-auto max-w-4xl"
        style={{ fontSize: "clamp(2.2rem, 6vw, 5.5rem)" }}
        data-testid="contact-closing-quote"
      >
        "Every great film starts with a single prompt."
      </blockquote>
      <p className="mt-10 font-serif italic text-bone/85 text-xl md:text-2xl max-w-2xl mx-auto">
        Let's talk. Every great film begins with a single conversation.
      </p>
      <div className="mt-12 flex flex-wrap items-center justify-center gap-5">
        <a
          href="mailto:samresh.shrivastav@gmail.com"
          className="cp-btn cp-btn--glow"
          data-testid="contact-closing-email"
        >
          <Mail size={12} /> samresh.shrivastav@gmail.com
        </a>
      </div>
    </div>
  </section>
);

/* ------------------ Page ------------------ */
const Contact = () => {
  return (
    <PageTransition testId="page-contact">
      <div className="relative bg-ink overflow-hidden">
        {/* Animated drifting glow blobs */}
        <div
          className="cp-glow-blob cp-glow-blob--gold"
          style={{ top: "-10%", right: "-8%", opacity: 0.3 }}
        />
        <div
          className="cp-glow-blob cp-glow-blob--ember"
          style={{ top: "30%", left: "-12%", opacity: 0.25 }}
        />
        {/* Slow particle field */}
        <Particles />

        <div className="relative z-10">
          <Header />
          <ContactSplit />
        </div>
      </div>
      <ClosingBand />
    </PageTransition>
  );
};

export default Contact;
